import { PageHeader, Stat, Stats, DataTable, FilterBar, Empty, Loading } from '../../components/office';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { menuApi, menuSectionsApi, modifiersApi } from '@verlo/shared/services/api';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { Switch } from '@verlo/shared/components/ui/switch';
import { Signal } from '@verlo/shared/components/ui/signal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@verlo/shared/components/ui/select';
import { toast } from 'sonner';
import { Plus, Copy, Pencil, Trash2 } from 'lucide-react';
import MenuItemDialog, { EMPTY_ITEM } from '../../components/menu/MenuItemDialog';
import { saveMenuItem } from '../../components/menu/saveMenuItem';
import { useSite, useAuthoringSite } from '../../contexts/SiteContext';
import { sectionName } from '../../lib/sectionName';

// Items (menu consolidation, wave 1): the one catalogue. This page replaces Menu, Products
// and Menu builder, which were the same `menu_items` documents seen through three different
// dialogs — whichever you opened, you were missing something. One table, one dialog.

const gbp = (v) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(v || 0);
const marginOf = (it) => (it.price > 0 ? ((it.price - (it.cost || 0)) / it.price) * 100 : null);
const marginTone = (m) => (m == null ? null : m >= 65 ? 'ok' : m >= 50 ? 'warn' : 'stop');

const MenuItemsPage = () => {
  // The header's site picker. The menu is per site, so this page is the one site's menu —
  // it used to be every site the user could see, merged, with "44 on the till" counting a
  // scope rather than a till.
  const { site } = useSite();
  // See `MenuStructurePage`: reading follows the picker, writing needs a venue named.
  const authoring = useAuthoringSite();
  const [items, setItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [modifiers, setModifiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('q') || '');
  const section = params.get('section') || 'all';
  const avail = params.get('available') || 'all';
  const [editing, setEditing] = useState(null);   // null | EMPTY_ITEM | item

  const load = async () => {
    setLoading(true);
    try {
      const [i, s, m] = await Promise.all([
        menuApi.getAll(undefined, site),
        menuSectionsApi.list(site).catch(() => ({ data: [] })),
        modifiersApi.getAll(site).catch(() => ({ data: [] })),
      ]);
      setItems(i.data);
      setSections(s.data || []);
      setModifiers((m.data || []).filter((x) => x.is_active !== false));
    } catch {
      toast.error("Couldn't load the menu");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [site]); // eslint-disable-line react-hooks/exhaustive-deps

  // `?item=<id>` (from Jump) opens that item once the list is in, then drops the param.
  useEffect(() => {
    const id = params.get('item');
    if (!id || loading) return;
    const it = items.find((x) => x.id === id);
    if (it) setEditing(it);
    const next = new URLSearchParams(params);
    next.delete('item');
    setParams(next, { replace: true });
  }, [items, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v || v === 'all') next.delete(k); else next.set(k, v);
    setParams(next, { replace: true });
  };

  const sectionOrder = useMemo(() => Object.fromEntries(sections.map((s, i) => [s.name, i])), [sections]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) =>
      (section === 'all' || it.category === section) &&
      (avail === 'all' || (avail === 'on' ? it.is_available !== false : it.is_available === false)) &&
      (!q || it.name.toLowerCase().includes(q) || (it.description || '').toLowerCase().includes(q)),
    );
  }, [items, section, avail, search]);

  const stats = useMemo(() => {
    const live = items.filter((it) => it.is_available !== false);
    const withCost = items.filter((it) => it.cost > 0 && it.price > 0);
    const avgMargin = withCost.length ? withCost.reduce((s, it) => s + marginOf(it), 0) / withCost.length : null;
    const thin = withCost.filter((it) => marginOf(it) < 50).length;
    return { total: items.length, live: live.length, avgMargin, thin, costed: withCost.length };
  }, [items]);

  const save = async (form) => {
    await saveMenuItem(form, authoring.site);
    setEditing(null);
    load();
  };

  const toggle = async (it) => {
    try {
      await menuApi.update(it.id, { ...it, is_available: it.is_available === false });
      setItems(items.map((x) => (x.id === it.id ? { ...x, is_available: it.is_available === false } : x)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update");
    }
  };

  const duplicate = (it) => setEditing({ ...it, id: undefined, name: `${it.name} (copy)` });

  const remove = async (it) => {
    if (!window.confirm(`Delete ${it.name}? It leaves every till at the next sync.`)) return;
    try {
      await menuApi.delete(it.id);
      toast.success(`${it.name} deleted`);
      setItems(items.filter((x) => x.id !== it.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    }
  };

  const columns = [
    { key: 'plu', label: 'PLU', sortable: true, mono: true, render: (it) => <span className="text-ink-soft tabular-nums">{it.plu ?? '—'}</span> },
    { key: 'name', label: 'Item', sortable: true, render: (it) => (
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0">
          <div className={`font-medium ${it.is_available === false ? 'text-ink-soft line-through' : 'text-ink'}`}>{it.name}</div>
          {it.description && <div className="max-w-md truncate text-[12.5px] text-ink-soft">{it.description}</div>}
        </div>
        {it.promoted && <Signal tone="info" dot>Promoted</Signal>}
        {(it.is_vegan || it.is_vegetarian || it.is_gluten_free) && (
          // At the row's end, so every row's marks line up (Chris's review L18).
          <span className="ml-auto shrink-0 font-mono text-[11px] text-ink-faint">{[it.is_vegan && 'VG', it.is_vegetarian && !it.is_vegan && 'V', it.is_gluten_free && 'GF'].filter(Boolean).join(' ')}</span>
        )}
      </div>
    ) },
    { key: 'category', label: 'Section', sortable: true, muted: true, sortValue: (it) => sectionOrder[it.category] ?? 999, render: (it) => sectionName(it.category) },
    { key: 'price', label: 'Price', align: 'right', mono: true, sortable: true, render: (it) => gbp(it.price) },
    { key: 'cost', label: 'Cost', align: 'right', mono: true, muted: true, sortable: true, render: (it) => (it.cost > 0 ? gbp(it.cost) : <span className="text-ink-faint">—</span>) },
    { key: 'margin', label: 'Margin', align: 'right', mono: true, sortable: true, sortValue: (it) => marginOf(it) ?? -1, render: (it) => {
      const m = marginOf(it);
      if (m == null || !(it.cost > 0)) return <span className="text-ink-faint">—</span>;
      // In words as well as colour (Chris's review L23).
      const word = { ok: 'healthy', warn: 'thin', stop: 'too low' }[marginTone(m)];
      return <Signal tone={marginTone(m)} title={`${m.toFixed(0)}% margin, ${word}`}>{m.toFixed(0)}%<span className="sr-only">, {word}</span></Signal>;
    } },
    { key: 'is_available', label: 'On till', align: 'right', sortable: true, sortValue: (it) => (it.is_available === false ? 0 : 1), render: (it) => (
      <span onClick={(e) => e.stopPropagation()}>
        <Switch checked={it.is_available !== false} onCheckedChange={() => toggle(it)} aria-label={`${it.name} on the till`} data-testid={`item-avail-${it.id}`} />
      </span>
    ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Items"
        purpose="Everything the till sells: price, cost and margin, in the sections the till shows."
        actions={(
          <Button className="bg-verlo hover:bg-verlo-hover" onClick={() => setEditing({ ...EMPTY_ITEM, category: section !== 'all' ? section : '' })} disabled={!authoring.ready} title={authoring.reason || undefined} data-testid="add-item-btn">
            <Plus size={16} className="mr-2" />
            Add item
          </Button>
        )}
      />

      {loading ? <Loading shape="table" /> : items.length === 0 ? (
        <Empty word="Items" title="Nothing on the menu yet" testId="items-empty"
          action={<Button variant="outline" onClick={() => setEditing(EMPTY_ITEM)}>Add the first item</Button>}>
          Every item needs a name, a price and a section. Cost is optional and gives you a margin.
        </Empty>
      ) : (
        <>
          <Stats>
            <Stat label="Items" value={stats.total} hint={`${stats.live} on the till`} />
            <Stat label="Costed" value={stats.costed} hint={stats.costed < stats.total ? `${stats.total - stats.costed} without a cost` : 'Every item'} />
            <Stat label="Average margin" value={stats.avgMargin == null ? '—' : `${stats.avgMargin.toFixed(0)}%`}
                  delta={stats.avgMargin == null ? undefined : { text: stats.avgMargin >= 65 ? 'Target 65%' : 'Below 65% target', tone: stats.avgMargin >= 65 ? 'ok' : 'warn' }} />
            <Stat label="Thin margins" value={stats.thin} delta={stats.thin ? { text: 'Under 50%', tone: 'stop' } : undefined} hint={stats.thin ? undefined : 'None under 50%'} />
          </Stats>

          <FilterBar right={<span>{rows.length} of {items.length}</span>}>
            <Input placeholder="Search items…" aria-label="Search items" value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" data-testid="items-search" />
            <Select value={section} onValueChange={(v) => setParam('section', v)}>
              <SelectTrigger className="h-9 w-[200px]" data-testid="items-section"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sections</SelectItem>
                {sections.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}{s.is_active === false ? ' (off)' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={avail} onValueChange={(v) => setParam('available', v)}>
              <SelectTrigger className="h-9 w-[150px]" data-testid="items-avail"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">On and off</SelectItem>
                <SelectItem value="on">On the till</SelectItem>
                <SelectItem value="off">Off the till</SelectItem>
              </SelectContent>
            </Select>
          </FilterBar>

          <DataTable
            scroll
            columns={columns}
            rows={rows}
            rowKey="id"
            defaultSort={{ key: 'category', dir: 'asc' }}
            onRowClick={setEditing}
            actions={(it) => (
              <>
                <Button variant="ghost" size="sm" className="h-8" onClick={() => setEditing(it)} title="Edit" aria-label={`Edit ${it.name}`} data-testid={`item-edit-${it.id}`}><Pencil size={14} /></Button>
                <Button variant="ghost" size="sm" className="h-8" onClick={() => duplicate(it)} title="Duplicate" aria-label={`Duplicate ${it.name}`} data-testid={`item-copy-${it.id}`}><Copy size={14} /></Button>
                <Button variant="ghost" size="sm" className="h-8 text-signal-stop hover:text-signal-stop" onClick={() => remove(it)} title="Delete" aria-label={`Delete ${it.name}`} data-testid={`item-delete-${it.id}`}><Trash2 size={14} /></Button>
              </>
            )}
            empty={{ word: 'Items', title: 'Nothing matches', action: <Button variant="outline" onClick={() => { setSearch(''); setParams({}, { replace: true }); }}>Clear filters</Button> }}
            testId="items-table"
          />
        </>
      )}

      <MenuItemDialog
        item={editing}
        sections={sections}
        modifiers={modifiers}
        menuItems={items}
        onClose={() => setEditing(null)}
        onSave={save}
      />
    </div>
  );
};

export default MenuItemsPage;
